import { useMemo } from 'react';
import { COLUMN, ROW, NODE_WIDTH, NODE_HEIGHT, V_GAP } from './orgChartHelpers';

/**
 * Positions every visible node and derives the connector paths.
 *
 * Leaf-counting layout: walk the tree post-order, give each leaf the next free
 * column, and centre every parent between its first and last child. Because all
 * cards are the same width this produces exactly the same result as a full
 * Reingold–Tilford pass — no overlaps, minimal width — in a fraction of the code.
 * It is O(n) and runs on ~670 nodes without noticeable cost.
 *
 * Connectors are orthogonal elbows drawn in a single SVG layer: down from the
 * parent, across a shared horizontal rail halfway down the gap, then down into
 * each child. That shared rail is what makes an org chart readable — one line
 * per child, all meeting at the same height.
 */

const layoutTree = (root) => {
  const nodes = [];
  const edges = [];
  let nextColumn = 0;

  const place = (node) => {
    const y = node.depth * ROW;
    let x;

    if (!node.children || node.children.length === 0) {
      x = nextColumn * COLUMN;
      nextColumn += 1;
    } else {
      const placedChildren = node.children.map(place);
      const first = placedChildren[0];
      const last = placedChildren[placedChildren.length - 1];
      x = (first.x + last.x) / 2;

      placedChildren.forEach((child) => {
        edges.push({
          id: `${node.id}->${child.id}`,
          parentX: x + NODE_WIDTH / 2,
          parentY: y + NODE_HEIGHT,
          childX: child.x + NODE_WIDTH / 2,
          childY: child.y,
          railY: y + NODE_HEIGHT + V_GAP / 2,
          dimmed: child.isContext,
        });
      });
    }

    const placed = { ...node, x, y };
    nodes.push(placed);
    return placed;
  };

  place(root);

  const width = Math.max(nextColumn, 1) * COLUMN;
  const height = nodes.reduce((max, n) => Math.max(max, n.y + NODE_HEIGHT), NODE_HEIGHT);

  return { nodes, edges, width, height };
};

const useOrgChartLayout = (tree) => useMemo(() => layoutTree(tree), [tree]);

export default useOrgChartLayout;
export { layoutTree };
